import { useEffect, useState } from 'react';

/**
 * 경과 시간 카운터 — 오래 걸리는 비동기 작업(AI 분석 등)에서 "멈춘 게 아니라 살아 있다"를
 * 정직하게 보여 주는 신호.
 *
 * 진행률을 추정해 바를 움직이는 방식(가짜 진행률)은 쓰지 않는다 — 서버가 모르는 진행을
 * 지어내는 것이라 100%에 도달한 뒤에도 안 끝나면 오히려 더 불안해진다. 움직이는 숫자
 * 하나가 가장 확실한 생존 신호다.
 */

/**
 * `active`가 true인 동안 경과 초를 1초 간격으로 센다. false가 되면 정지 + 0으로 리셋.
 *
 * 누적 카운터가 아니라 **기준 시각에서 매번 다시 계산**한다. 그래서
 * ① 탭 백그라운드 등으로 타이머가 밀려도 표시가 실제 경과와 어긋나지 않고,
 * ② StrictMode 이중 실행처럼 effect가 두 번 돌아도 값이 두 배로 뛰지 않는다
 *    (증분이 아니라 절대 시각 기준이라 몇 번을 계산해도 같은 값).
 *
 * @param knownElapsedMs 이미 지나 있던 시간(ms). 새로고침 복구처럼 **화면에 들어오기 전부터
 *   진행 중이던** 작업에서, 서버가 알려 준 경과를 이어받아 0이 아닌 값부터 세게 한다.
 *   서버의 절대 시각이 아니라 **상대 경과**를 받는 이유는 클라이언트 PC 시계 오차를 표시에
 *   싣지 않기 위해서다. 값은 **활성화당 한 번만 채택**한다 — 폴링마다 다시 잡으면 기준이
 *   흔들려 카운터가 떨린다.
 */
export function useElapsedSeconds(active: boolean, knownElapsedMs?: number | null): number {
  const [seconds, setSeconds] = useState(0);
  const [adoptedElapsedMs, setAdoptedElapsedMs] = useState<number | null>(null);

  // 서버가 알려 준 경과를 최초 1회만 채택(비활성으로 돌아가면 다음 활성화를 위해 비운다).
  useEffect(() => {
    if (!active) {
      setAdoptedElapsedMs(null);
      return;
    }
    if (adoptedElapsedMs === null && typeof knownElapsedMs === 'number') {
      setAdoptedElapsedMs(knownElapsedMs);
    }
  }, [active, knownElapsedMs, adoptedElapsedMs]);

  useEffect(() => {
    if (!active) {
      setSeconds(0);
      return;
    }
    // 채택한 경과만큼 과거로 기준점을 당긴다 — 없으면(옛 job·정보 없음) 화면 진입 시각.
    const startedAt = Date.now() - (adoptedElapsedMs ?? 0);
    const read = () => Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
    setSeconds(read());
    const timer = setInterval(() => setSeconds(read()), 1000);
    return () => clearInterval(timer);
  }, [active, adoptedElapsedMs]);

  return seconds;
}

/** 경과 초 → 사람이 읽는 한글 표기 (`35초`, `1분 35초`, 정확히 분 단위면 `2분`). */
export function formatElapsed(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  if (total < 60) return `${total}초`;
  const minutes = Math.floor(total / 60);
  const rest = total % 60;
  return rest === 0 ? `${minutes}분` : `${minutes}분 ${rest}초`;
}
